import React from 'react';
import { useUiStore } from '../stores/useUiStore';

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  handleReload = () => {
    window.location.reload();
  };

  handleSwitchTo3D = () => {
    useUiStore.getState().setViewMode('3d');
    this.setState({ hasError: false, error: undefined });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex h-full w-full items-center justify-center bg-surface">
          <div className="rounded-xl bg-slate-800/80 p-6 text-center shadow-lg border border-slate-700/50 max-w-sm mx-4">
            <h2 className="text-lg font-semibold text-slate-200">Something went wrong</h2>
            <p className="mt-2 text-sm text-slate-400">
              {this.state.error?.message ?? 'An unexpected error occurred.'}
            </p>
            <div className="mt-4 flex items-center justify-center gap-3">
              <button
                onClick={this.handleReload}
                className="rounded-md bg-sky-900/40 px-4 py-2 text-sm font-medium text-sky-300 hover:bg-sky-900/60 transition-colors border border-sky-700/30"
              >
                Reload
              </button>
              <button
                onClick={this.handleSwitchTo3D}
                className="rounded-md bg-slate-700/50 px-4 py-2 text-sm font-medium text-slate-300 hover:bg-slate-700/70 transition-colors border border-slate-600/30"
              >
                Switch to 3D View
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
